import * as React from "react";
import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Form, Formik } from "formik";
import { Button, Col, Modal, Row } from "components/bootstrap";
import FormikInput from "components/common/FormikInput";
import Spinner from "components/common/Spinner";
import { InputDescription, ModalSubmit, IfPermitted } from "components/common";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { FailureActions } from "./stores/FailureStore";
const StyledDefList = styled.dl.attrs({
  className: "deflist"
})(
  ({ theme }) => css`
    &&.deflist {
      dd {
        padding-left: ${theme.spacings.md};
        margin-left: 200px;
      }
    }
  `
);
const LabelSpan = styled.span(
  ({ theme }) => css`
    margin-left: ${theme.spacings.sm};
    font-weight: bold;
  `
);
const handleLoadFailureConfig = () => FailureActions.loadFailureConfig();
const handleSaveFailureConfig = (config) => FailureActions.updateFailureConfig(config);
const FailureProcessingConfig = () => {
  const [failureConfig, setFailureConfig] = useState();
  const [showModal, setShowModal] = useState(false);
  const sendTelemetry = useSendTelemetry();
  const _saveConfig = (values) => {
    handleSaveFailureConfig(values).then(() => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.FAILURE_PROCESSING_CONFIGURATION_SAVED, {
        app_pathname: "configurations",
        app_section: "failure-processing"
      });
      setShowModal(false);
    });
  };
  const _resetConfig = () => {
    setShowModal(false);
  };
  useEffect(() => {
    if (!showModal) {
      handleLoadFailureConfig().then((loadedConfig) => {
        setFailureConfig(loadedConfig);
      });
    }
  }, [showModal]);
  const modalTitle = "Configure Failure Processing";
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, "Failure Processing"), /* @__PURE__ */ React.createElement("p", null, "Configuration for Failure Processing to enable / disable handling of individual failure types and their features"), !failureConfig ? /* @__PURE__ */ React.createElement(Spinner, null) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledDefList, null, /* @__PURE__ */ React.createElement("dt", null, "Log Indexing Failures:"), /* @__PURE__ */ React.createElement("dd", null, failureConfig.persistIndexingFailure ? "Enabled" : "Disabled"), /* @__PURE__ */ React.createElement("dt", null, "Log Processing Failures:"), /* @__PURE__ */ React.createElement("dd", null, failureConfig.submitProcessingFailure ? "Enabled" : "Disabled"), /* @__PURE__ */ React.createElement("dt", null, "Log Input Failures:"), /* @__PURE__ */ React.createElement("dd", null, failureConfig.persistInputFailure ? "Enabled" : "Disabled"), /* @__PURE__ */ React.createElement("dt", null, "Include Failed Messages:"), /* @__PURE__ */ React.createElement("dd", null, failureConfig.persistFailedMessage ? "Enabled" : "Disabled"), /* @__PURE__ */ React.createElement("dt", null, "Continue processing on error:"), /* @__PURE__ */ React.createElement("dd", null, failureConfig.keepFailedMessageDuplicate ? "Enabled" : "Disabled")), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "clusterconfigentry:edit" }, /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
    Button,
    {
      type: "button",
      bsSize: "xs",
      bsStyle: "info",
      onClick: () => {
        setShowModal(true);
      }
    },
    "Edit configuration"
  ))), /* @__PURE__ */ React.createElement(Modal, { show: showModal, onHide: _resetConfig }, /* @__PURE__ */ React.createElement(Formik, { onSubmit: _saveConfig, initialValues: failureConfig }, ({ isSubmitting, values }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, modalTitle)), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "persistIndexingFailure",
      id: "persistIndexingFailure",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, "Log Indexing Failures")
    }
  ), /* @__PURE__ */ React.createElement(InputDescription, { help: /* @__PURE__ */ React.createElement(React.Fragment, null, "Indexing errors will be stored in Elasticsearch") })), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "submitProcessingFailure",
      id: "submitProcessingFailure",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, "Log Processing Failures")
    }
  ), /* @__PURE__ */ React.createElement(
    InputDescription,
    {
      help: /* @__PURE__ */ React.createElement(React.Fragment, null, "Processing errors (e.g. pipeline rule evaluation errors) will be stored in Elasticsearch")
    }
  )), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "persistInputFailure",
      id: "persistInputFailure",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, "Log Input Failures")
    }
  ), /* @__PURE__ */ React.createElement(InputDescription, { help: /* @__PURE__ */ React.createElement(React.Fragment, null, "Input errors will be stored in Elasticsearch") })), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "persistFailedMessage",
      id: "persistFailedMessage",
      disabled: !values.submitProcessingFailure && !values.persistIndexingFailure,
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, "Include Failed Messages")
    }
  ), /* @__PURE__ */ React.createElement(InputDescription, { help: /* @__PURE__ */ React.createElement(React.Fragment, null, "Include the problematic message into the failure") })), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "keepFailedMessageDuplicate",
      id: "keepFailedMessageDuplicate",
      disabled: !values.submitProcessingFailure,
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, "Continue processing on error")
    }
  ), /* @__PURE__ */ React.createElement(
    InputDescription,
    {
      help: /* @__PURE__ */ React.createElement(React.Fragment, null, "If a pipeline rule raises an error, a ", /* @__PURE__ */ React.createElement("code", null, "gl2_processing_error"), " field will be attached to the original message and regular processing we be continued")
    }
  ))))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      onCancel: _resetConfig,
      isSubmitting,
      submitButtonText: "Update configuration",
      submitLoadingText: "Updating configuration..."
    }
  )))))));
};
export default FailureProcessingConfig;
